import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	SearchInput,
	SearchPlaceholder,
	Toolbar
} from "client/Components";
import { SelectUserAndUserGroupDialog } from "./SelectUserAndUserGroupDialog";
import { localize } from "client_legacy/ndaw/culture";
import { unique } from "client_legacy/ndaw/arrayHelpers";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { ActiveTab, UserAndUserGroupTabSelector } from "../UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import styles from "./UserAndUserGroupCollection.module.less";
import {
	UserGroupCollection,
	UserGroupCollectionMode,
	userGroupCollectionSearchPropertyKeys
} from "./UserGroupCollection";
import { UserCollection, userCollectionSearchPropertyKeys } from "./UserCollection";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { UserAndUserGroupSelection } from "./UserAndUserGroupOverviews";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";

export interface UserAndUserGroupCollectionValue {
	userIds: readonly number[];
	userGroupIds: readonly number[];
}

export enum UserAndUserGroupKind {
	Read = 1,
	Edit = 2
}

interface UserAndUserGroupCollectionReadMode {
	kind: UserAndUserGroupKind.Read;
}

export interface UserAndUserGroupCollectionEditMode {
	kind: UserAndUserGroupKind.Edit;
	dialogTitleWhenAddingUsersAndUserGroups: string;
	onChange(newValue: UserAndUserGroupCollectionValue): void;
}

export type UserAndUserGroupCollectionMode = UserAndUserGroupCollectionReadMode | UserAndUserGroupCollectionEditMode;

export type UserAndUserGroupCollectionProps = {
	allUsersAndUserGroupsData: UsersAndUserGroupsDataModel;
	value: UserAndUserGroupCollectionValue;
	activeTab: ActiveTab;
	mode: UserAndUserGroupCollectionMode;
	requiredUserLicense?: UserLicense | null;
};

export const UserAndUserGroupCollection = (props: UserAndUserGroupCollectionProps): ReactElement => {
	const {
		allUsersAndUserGroupsData,
		value,
		activeTab,
		mode,
		requiredUserLicense
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const [currentActiveTab, setCurrentActiveTab] = useState<ActiveTab>(activeTab);

	const userGroupCollectionMode: UserGroupCollectionMode = useMemo(() => {
		switch(mode.kind) {
			case UserAndUserGroupKind.Edit:
				return {
					kind: UserAndUserGroupKind.Edit,
					onChange: (userGroupIds: readonly number[]): void => {
						mode.onChange({ ...value, userGroupIds });
					}
				};
			case UserAndUserGroupKind.Read:
				return {
					kind: UserAndUserGroupKind.Read
				};
			default:
				throw new UnreachableCaseError(mode);
		}
	}, [mode, value]);

	const userCollectionMode: UserGroupCollectionMode = useMemo(() => {
		switch(mode.kind) {
			case UserAndUserGroupKind.Edit:
				return {
					kind: UserAndUserGroupKind.Edit,
					onChange: (userIds: readonly number[]): void => {
						mode.onChange({ ...value, userIds });
					}
				};
			case UserAndUserGroupKind.Read:
				return {
					kind: UserAndUserGroupKind.Read
				};
			default:
				throw new UnreachableCaseError(mode);
		}
	}, [mode, value]);

	const selectedHandler = (collection: UserAndUserGroupSelection, lastActiveTab: ActiveTab): void =>{
		if (mode.kind === UserAndUserGroupKind.Read) {
			throw new Error("Add is not supported in read mode");
		}
		mode.onChange({
			userIds: unique(value.userIds.concat(collection.users.map(x => x.id))),
			userGroupIds: unique(value.userGroupIds.concat(collection.userGroups.map(x => x.id)))
		});
		setCurrentActiveTab(lastActiveTab);
		setIsDialogOpen(false);
	};

	return (
		<div className={styles.root}>
			{ mode.kind === UserAndUserGroupKind.Edit && (
				<div className={styles.buttonList}>
					<Toolbar>
						<Toolbar.Button
							trackingId="add-users-or-groups"
							kind="secondary"
							icon="plus"
							text={localize("_Add")}
							onClick={() => setIsDialogOpen(true)}
						/>
					</Toolbar>
				</div>
			)}
			<div className={styles.searchAndTabSelection}>
				<div className={styles.search}>
					<SearchInput
						trackingId="search"
						onChange={setSearchTerm}
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={
									activeTabToPropertiesMap[currentActiveTab]
										.map(localize)
								}
							/>
						)}
					/>
				</div>
				<div className={styles.tabSelector}>
					<UserAndUserGroupTabSelector
						value={currentActiveTab}
						onChange={setCurrentActiveTab}
					/>
				</div>
			</div>
			<div className={styles.tableContainer}>
				{currentActiveTab === ActiveTab.UserGroup && (
					<UserGroupCollection
						allData={allUsersAndUserGroupsData.userGroups}
						value={value.userGroupIds}
						searchTerm={searchTerm}
						mode={userGroupCollectionMode}
						height="stretch"
					/>
				)}

				{currentActiveTab === ActiveTab.User && (
					<UserCollection
						allData={allUsersAndUserGroupsData.users}
						value={value.userIds}
						searchTerm={searchTerm}
						mode={userCollectionMode}
						height="stretch"
						requiredUserLicense={requiredUserLicense}
					/>
				)}
			</div>
			{ (isDialogOpen && mode.kind === UserAndUserGroupKind.Edit) && (
				<SelectUserAndUserGroupDialog
					title={mode.dialogTitleWhenAddingUsersAndUserGroups}
					initialActiveTab={currentActiveTab}
					data={allUsersAndUserGroupsData}
					onSelected={selectedHandler}
					onClose={() => setIsDialogOpen(false)}
					requiredUserLicense={requiredUserLicense}
				/>
			)}
		</div>
	);
};

UserAndUserGroupCollection.displayName = "UserAndUserGroupCollection";

const activeTabToPropertiesMap: Record<ActiveTab, readonly [LocalizationKey, ...LocalizationKey[]]> = {
	[ActiveTab.User]: userCollectionSearchPropertyKeys,
	[ActiveTab.UserGroup]: userGroupCollectionSearchPropertyKeys
};
